import { ApplicationView, raw, useController } from '@aha-app/mvc';
import React, { useState } from 'react';
import { AsyncCreatable, Async, components } from '@aha-app/react-select';
import { type FieldInputProps } from '.';
import FieldsController from '../../../controllers/FieldsController';
import type SelectField from '../../../fields/SelectField';
import { type RecordId } from '../../../types';

interface ReactSelectOption {
  label: string;
  value: string;
  id?: RecordId;
}

// "no unused vars" disabled because this forked third-party component will
// render children if passed them and it looks like we don't want that to happen.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NoOptionsMessage: React.FC<any> = ({ children, ...props }) => {
  return <components.NoOptionsMessage {...props} />;
};

const SelectFieldInput: React.FC<FieldInputProps & { field: SelectField }> = ({
  field,
  objects,
  value,
  sameValue,
}) => {
  const controller = useController(FieldsController);
  const [open, setOpen] = useState(false);
  const selected = field.options.items.find(option => option.id === value);

  if (field.tags && !field.editable) {
    return (
      <aha-pill color={`${selected?.color}88`} border-color={selected?.color}>
        <select
          value={value || ''}
          onChange={event =>
            controller.actionUpdateObjectsField(
              objects,
              field,
              event.target.value
            )
          }
          style={{
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            height: 'auto',
          }}
          className='py-05 px-0 m-0'
        >
          <option>{sameValue ? 'No value' : 'Multiple values'}</option>
          {field.options.items.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </aha-pill>
    );
  }

  if (!open) {
    return (
      <div
        className='react-in-place__tags react-in-place react-in-place--block react-in-place__placeholder react-in-place--dark-tags react-in-place__tags--single'
        tabIndex={1}
        onClick={() => setOpen(true)}
      >
        <div className='react-in-place__tags__single-value'>
          {sameValue ? selected?.name : 'Multiple values'}
        </div>
      </div>
    );
  }

  const SelectComponent = field.editable ? AsyncCreatable : Async;

  return (
    <div className='react-in-place-wrapper react-in-place-wrapper--block '>
      <div className='react-in-place__tags react-in-place react-in-place--block react-in-place__placeholder react-in-place--active react-in-place--dark-tags react-in-place__tags--single'>
        <SelectComponent
          defaultOptions
          cacheOptions
          openMenuOnFocus
          menuIsOpen
          autoFocus
          isMulti={false}
          menuPosition='fixed'
          menuShouldBlockScroll
          onChange={(option: ReactSelectOption) => {
            // Existing options will have an id, creating a new one will not
            let id;
            if ('id' in option) {
              id = option.id;
            } else if (field.editable) {
              id = field.addOption({ name: option.label });
            } else {
              // Invalid value, can't add one
              return;
            }

            controller.actionUpdateObjectsField(objects, field, id);

            setOpen(false);
          }}
          onBlur={() => setOpen(false)}
          className='react-in-place__tags__container '
          classNamePrefix='react-in-place__tags'
          placeholder=''
          blurInputOnSelect={false}
          closeMenuOnSelect={false}
          loadOptions={async query => {
            query = (query || '').toLowerCase();

            return await Promise.resolve(
              raw(field)
                .options.items.filter(({ name }) => {
                  if (!query) return true;
                  if (typeof name !== 'string') return false;

                  return name.toLowerCase().includes(query);
                })
                .sort((a, b) => {
                  const aName = a.name;
                  const bName = b.name;

                  if (typeof aName !== 'string') return 1;
                  if (typeof bName !== 'string') return -1;

                  return aName.slice().toLowerCase() < bName.toLowerCase()
                    ? -1
                    : 1;
                })
                .map(
                  ({ name, id }): ReactSelectOption => ({
                    label: name,
                    value: id,
                    id,
                  })
                )
            );
          }}
          styles={{
            control: base => ({
              ...base,
              boxShadow: 'none',
              borderRadius: 0,
              background: 'none',
              minHeight: 'auto',
              height: '100%',
              border: 'none',
            }),
            menuPortal: base => {
              const styles = {
                ...base,
                zIndex: 950,
                minWidth: 250,
              };
              return styles;
            },
          }}
          components={{
            DropdownIndicator: null,
            ClearIndicator: null,
            LoadingIndicator: null,
            NoOptionsMessage,
          }}
        />
      </div>
    </div>
  );
};

export default ApplicationView(SelectFieldInput);
